// Step 5 of the client-side X12 pipeline (210 variant): walks one
// transaction's flat body segments (from step 2's envelope tree) into the
// loop structure a human reader thinks in — header (B3/C3/ITD/REF/G62 and a
// header-level Name loop), a repeating Stop (LX) loop each nesting its own
// Name loop, dates (G62), description (L5), weight detail (L0) and
// freight/accessorial charge lines (L1), and an L3 summary. Only ever runs
// against a transaction whose ST01 is "210"; anything else is left alone so
// it can still be shown, read-only, by the UI. The 210 carries every date on
// G62 rather than DTM, and REF only ever occurs at header level in this
// model (never within a Name or Stop loop) — mirrors parser845.ts's
// top-level nameLoops phase-switch pattern for the header, generalized the
// way parser850.ts nests a Name sub-loop inside its repeating item loop to
// nest a Name loop (plus dates and charge lines) inside the repeating Stop
// loop.

import type {EdiSegment} from './tokenizer';
import type {EdiEnvelope, EdiGroup, EdiTransaction} from './envelope';

export interface Header210 {
  b3?: EdiSegment;
  c3?: EdiSegment;
  itd?: EdiSegment;
  refs: EdiSegment[];
  g62: EdiSegment[];
  nameLoops: NameLoop210[];
  other: EdiSegment[];
}

export interface NameLoop210 {
  n1: EdiSegment;
  n2?: EdiSegment;
  n3?: EdiSegment;
  n4?: EdiSegment;
  per?: EdiSegment;
  other: EdiSegment[];
}

export interface StopLoop210 {
  lx: EdiSegment;
  nameLoops: NameLoop210[];
  g62: EdiSegment[];
  l5?: EdiSegment;
  l0?: EdiSegment;
  l1: EdiSegment[];
  other: EdiSegment[];
}

export interface Summary210 {
  l3: EdiSegment[];
}

export interface Parsed210 {
  header: Header210;
  stops: StopLoop210[];
  summary: Summary210;
}

function attachNameLoopSegment(loop: NameLoop210, s: EdiSegment): void {
  switch (s.id) {
    case 'N2':
      loop.n2 = s;
      break;
    case 'N3':
      loop.n3 = s;
      break;
    case 'N4':
      loop.n4 = s;
      break;
    case 'PER':
      loop.per = s;
      break;
    default:
      loop.other.push(s);
      break;
  }
}

function emptyHeader(): Header210 {
  return {refs: [], g62: [], nameLoops: [], other: []};
}

function emptyStop(lx: EdiSegment): StopLoop210 {
  return {lx, nameLoops: [], g62: [], l1: [], other: []};
}

type Phase = 'header' | 'name' | 'stop';
type StopSubPhase = 'main' | 'name';

/**
 * Walks one 210 transaction's flat body segments (ST/SE excluded) into its
 * loop structure. Never throws — a segment that doesn't fit anywhere
 * sensible in the current loop is bucketed into that loop's `other` list
 * rather than the walk aborting; the validator is what reports on a
 * document that's missing or misordered.
 */
export function parse210Segments(segments: EdiSegment[]): Parsed210 {
  const header = emptyHeader();
  const stops: StopLoop210[] = [];
  const summary: Summary210 = {l3: []};

  let phase: Phase = 'header';
  let stopSubPhase: StopSubPhase = 'main';
  let currentNameLoop: NameLoop210 | undefined;
  let currentStop: StopLoop210 | undefined;

  for (const s of segments) {
    switch (s.id) {
      case 'B3':
        header.b3 = s;
        break;

      case 'C3':
        header.c3 = s;
        break;

      case 'ITD':
        header.itd = s;
        break;

      case 'REF':
        header.refs.push(s);
        break;

      case 'N1':
        if (phase === 'stop' && currentStop) {
          stopSubPhase = 'name';
          currentNameLoop = {n1: s, other: []};
          currentStop.nameLoops.push(currentNameLoop);
        } else {
          phase = 'name';
          currentNameLoop = {n1: s, other: []};
          header.nameLoops.push(currentNameLoop);
        }
        break;

      case 'N2':
      case 'N3':
      case 'N4':
      case 'PER':
        if (stopSubPhase === 'name' && currentNameLoop) {
          attachNameLoopSegment(currentNameLoop, s);
        } else if (phase === 'name' && currentNameLoop) {
          attachNameLoopSegment(currentNameLoop, s);
        } else if (phase === 'stop' && currentStop) {
          currentStop.other.push(s);
        } else {
          header.other.push(s);
        }
        break;

      case 'LX':
        phase = 'stop';
        stopSubPhase = 'main';
        currentNameLoop = undefined;
        currentStop = emptyStop(s);
        stops.push(currentStop);
        break;

      case 'G62':
        stopSubPhase = 'main';
        if (phase === 'stop' && currentStop) {
          currentStop.g62.push(s);
        } else {
          header.g62.push(s);
        }
        break;

      case 'L5':
        stopSubPhase = 'main';
        if (currentStop) {
          if (currentStop.l5) {
            currentStop.other.push(s);
          } else {
            currentStop.l5 = s;
          }
        } else if (currentNameLoop) {
          currentNameLoop.other.push(s);
        } else {
          header.other.push(s);
        }
        break;

      case 'L0':
        stopSubPhase = 'main';
        if (currentStop) {
          if (currentStop.l0) {
            currentStop.other.push(s);
          } else {
            currentStop.l0 = s;
          }
        } else if (currentNameLoop) {
          currentNameLoop.other.push(s);
        } else {
          header.other.push(s);
        }
        break;

      case 'L1':
        stopSubPhase = 'main';
        if (currentStop) {
          currentStop.l1.push(s);
        } else if (currentNameLoop) {
          currentNameLoop.other.push(s);
        } else {
          header.other.push(s);
        }
        break;

      case 'L3':
        summary.l3.push(s);
        currentStop = undefined;
        currentNameLoop = undefined;
        break;

      default:
        if (stopSubPhase === 'name' && currentNameLoop) {
          currentNameLoop.other.push(s);
        } else if (phase === 'stop' && currentStop) {
          currentStop.other.push(s);
        } else if (phase === 'name' && currentNameLoop) {
          currentNameLoop.other.push(s);
        } else {
          header.other.push(s);
        }
        break;
    }
  }

  return {header, stops, summary};
}

export interface EdiTransactionWithParsed210 extends EdiTransaction {
  /** Left undefined for a transaction whose ST01 isn't "210" — it keeps only
   * its raw segment list, for read-only display. */
  parsed210?: Parsed210;
}

export interface EdiGroupWithParsed210 extends Omit<EdiGroup, 'transactions'> {
  transactions: EdiTransactionWithParsed210[];
}

export interface EdiEnvelopeWithParsed210 extends Omit<EdiEnvelope, 'groups'> {
  groups: EdiGroupWithParsed210[];
}

/**
 * Parses every 210 transaction in an envelope tree (step 2's output),
 * attaching `parsed210` to each. A non-210 transaction is passed through
 * unchanged, `parsed210` left undefined.
 */
export function attachParsed210(
  envelope: EdiEnvelope
): EdiEnvelopeWithParsed210 {
  return {
    ...envelope,
    groups: envelope.groups.map(group => ({
      ...group,
      transactions: group.transactions.map(transaction => ({
        ...transaction,
        parsed210:
          transaction.transactionSetCode === '210'
            ? parse210Segments(transaction.segments)
            : undefined,
      })),
    })),
  };
}
